import { FormEvent, useState } from 'react';
import { Alert, TextField } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import LockResetIcon from '@mui/icons-material/LockReset';
import { isAxiosError } from 'axios';
import { apiErrorMessage } from 'api/axios_instance';
import useProfile from 'api/auth/useProfile';
import { FormActions, FormCard } from './AccountView.styles';

const MIN_PASSWORD_LENGTH = 8;

const emptyForm = { currentPassword: '', newPassword: '', confirmPassword: '' };

const ChangePasswordSection = () => {
  const {
    updatePassword: { mutateAsync, isPending },
  } = useProfile();

  const [form, setForm] = useState(emptyForm);
  const [currentPasswordError, setCurrentPasswordError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const setField = (field: keyof typeof emptyForm) => (value: string) => {
    setForm((previous) => ({ ...previous, [field]: value }));
    setCurrentPasswordError(null);
    setFormError(null);
  };

  const tooShort = form.newPassword.length > 0 && form.newPassword.length < MIN_PASSWORD_LENGTH;
  const mismatch = form.confirmPassword.length > 0 && form.newPassword !== form.confirmPassword;
  const canSubmit =
    !!form.currentPassword &&
    form.newPassword.length >= MIN_PASSWORD_LENGTH &&
    form.newPassword === form.confirmPassword;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    setCurrentPasswordError(null);
    setFormError(null);
    try {
      await mutateAsync({ current_password: form.currentPassword, new_password: form.newPassword });
      setForm(emptyForm);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 400) {
        setCurrentPasswordError('Current password is incorrect.');
        return;
      }
      setFormError(apiErrorMessage(error, 'Changing the password failed.'));
    }
  };

  return (
    <FormCard onSubmit={(event) => void handleSubmit(event)} noValidate>
      {formError && <Alert severity="error">{formError}</Alert>}

      <TextField
        label="Current password"
        type="password"
        size="small"
        value={form.currentPassword}
        onChange={(event) => setField('currentPassword')(event.target.value)}
        error={!!currentPasswordError}
        helperText={currentPasswordError ?? ' '}
        autoComplete="current-password"
        fullWidth
      />
      <TextField
        label="New password"
        type="password"
        size="small"
        value={form.newPassword}
        onChange={(event) => setField('newPassword')(event.target.value)}
        error={tooShort}
        helperText={tooShort ? `Use at least ${MIN_PASSWORD_LENGTH} characters.` : ' '}
        autoComplete="new-password"
        fullWidth
      />
      <TextField
        label="Confirm new password"
        type="password"
        size="small"
        value={form.confirmPassword}
        onChange={(event) => setField('confirmPassword')(event.target.value)}
        error={mismatch}
        helperText={mismatch ? 'Passwords do not match.' : ' '}
        autoComplete="new-password"
        fullWidth
      />

      <FormActions>
        <LoadingButton
          type="submit"
          variant="contained"
          startIcon={<LockResetIcon />}
          loading={isPending}
          loadingPosition="start"
          disabled={!canSubmit}
        >
          Change password
        </LoadingButton>
      </FormActions>
    </FormCard>
  );
};

export default ChangePasswordSection;
